// GuestDetailDrawer — opened by a guest row click (and the row kebab's Edit).
// Shows the guest profile (avatar + name + email) and a Details card that reads
// as label/value until Edit flips First/Last/Email/Phone into inputs. Dirty-tracked
// footer (All changes saved · Discard · Save changes). Fetches GET /guests/:id and
// saves via PATCH /guests/:id. The single edit surface for guests (Add is separate).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Avatar, Button, Card, Drawer, Field, Icon, Input, PhoneInput } from '@/components/common';
import { countries, defaultCountry } from '@/mocks/countries';
import { useGetGuestDetailQuery, useUpdateGuestMutation } from '@/api/endpoints/peopleApi';
import useModuleAccess from '@/hooks/useModuleAccess';
import initialsOf from '@/utils/initials';

interface GuestDetailDrawerProps {
  open: boolean;
  guestId?: string;
  /** Open straight into edit mode (e.g. from the row kebab's Edit). */
  startInEdit?: boolean;
  onClose: () => void;
}

const emptyDraft = {
  firstName: '',
  lastName: '',
  dialCode: defaultCountry.dial,
  mobileNumber: '',
  email: '',
};

const GuestDetailDrawer = ({ open, guestId, startInEdit, onClose }: GuestDetailDrawerProps) => {
  const { t } = useTranslation();
  const { data: detail, isFetching } = useGetGuestDetailQuery(guestId as string, {
    skip: !open || !guestId,
    refetchOnMountOrArgChange: true,
  });
  const [updateGuest, { isLoading: saving }] = useUpdateGuestMutation();

  // Without `edit` the drawer is read-only: no Edit flip, and `startInEdit`
  // (belt and braces — the kebab Edit entry is already gated) is ignored.
  const { canEdit } = useModuleAccess('people');

  const [draft, setDraft] = useState(emptyDraft);
  const [initial, setInitial] = useState(emptyDraft);
  const [editing, setEditing] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // On open, start in read mode — or straight in edit mode (kebab Edit).
  useEffect(() => {
    if (open) {
      setEditing(!!startInEdit && canEdit);
      setSubmitted(false);
    }
  }, [open, startInEdit, canEdit]);

  // Seed draft + baseline from the fetched guest (re-seeds after a save refetch).
  useEffect(() => {
    if (!detail) return;
    const seeded = {
      firstName: detail.firstName,
      lastName: detail.lastName,
      dialCode: detail.countryCode || defaultCountry.dial,
      mobileNumber: detail.mobileNumber ?? '',
      email: detail.email,
    };
    setDraft(seeded);
    setInitial(seeded);
  }, [detail]);

  const patch = (changes: Partial<typeof emptyDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const dirty =
    draft.firstName !== initial.firstName ||
    draft.lastName !== initial.lastName ||
    draft.dialCode !== initial.dialCode ||
    draft.mobileNumber !== initial.mobileNumber ||
    draft.email !== initial.email;

  const firstMissing = !draft.firstName.trim();
  const lastMissing = !draft.lastName.trim();
  const contactMissing = !draft.email.trim() && !draft.mobileNumber.trim();
  const valid = !firstMissing && !lastMissing && !contactMissing;

  const discard = () => {
    setDraft(initial);
    setSubmitted(false);
    setEditing(false);
  };

  const save = async () => {
    setSubmitted(true);
    if (!guestId || !dirty || !valid) return;
    const hasPhone = !!draft.mobileNumber.trim();
    try {
      await updateGuest({
        id: guestId,
        body: {
          firstName: draft.firstName.trim(),
          lastName: draft.lastName.trim(),
          email: draft.email.trim() || undefined,
          ...(hasPhone
            ? { countryCode: draft.dialCode, mobileNumber: draft.mobileNumber.trim() }
            : {}),
        },
      }).unwrap();
      toast.success(t('add_guest_updated'));
      setEditing(false);
      setSubmitted(false);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const loading = isFetching && !detail;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className='guest-detail'
      icon='user-round'
      title={t('guest_profile_title')}
      footer={
        <div className='guest-detail__footer'>
          {!dirty && (
            <span className='guest-detail__saved'>
              <Icon name='check' size={15} />
              {t('guest_all_saved')}
            </span>
          )}
          {canEdit && (
            <span className='guest-detail__footer-actions'>
              <Button variant='secondary' onClick={discard} disabled={!dirty || saving}>
                {t('guest_discard')}
              </Button>
              <Button
                iconStart='check'
                onClick={save}
                loading={saving}
                disabled={!editing || !dirty || !valid}
              >
                {t('guest_save_changes')}
              </Button>
            </span>
          )}
        </div>
      }
    >
      {loading ? (
        <div className='guest-detail__loading'>
          <Icon name='loader' size={26} />
          {t('text_loading')}
        </div>
      ) : (
        detail && (
          <div className='guest-detail__body'>
            {/* ---- profile card ---- */}
            <Card className='guest-detail__profile'>
              <Avatar initials={initialsOf(detail.fullName)} size={48} />
              <span className='guest-detail__identity'>
                <span className='guest-detail__name'>{detail.fullName}</span>
                {detail.email && <span className='guest-detail__email'>{detail.email}</span>}
              </span>
            </Card>

            {/* ---- details card ---- */}
            <Card
              padded={false}
              head={
                <div className='guest-detail__details-head'>
                  <span className='guest-detail__details-title'>{t('guest_details_title')}</span>
                  {!editing && canEdit && (
                    <Button
                      variant='secondary'
                      iconStart='pencil'
                      onClick={() => setEditing(true)}
                      size='sm'
                    >
                      {t('action_edit')}
                    </Button>
                  )}
                </div>
              }
            >
              <div className='guest-detail__details-body'>
                {editing ? (
                  <>
                    <div className='guest-detail__row'>
                      <Field
                        label={t('add_person_firstname')}
                        required
                        error={
                          submitted && firstMissing ? t('add_person_err_firstname') : undefined
                        }
                      >
                        <Input
                          value={draft.firstName}
                          placeholder={t('add_person_firstname_ph')}
                          error={submitted && firstMissing}
                          onChange={(e) => patch({ firstName: e.target.value })}
                        />
                      </Field>
                      <Field
                        label={t('add_person_lastname')}
                        required
                        error={submitted && lastMissing ? t('add_person_err_lastname') : undefined}
                      >
                        <Input
                          value={draft.lastName}
                          placeholder={t('add_person_lastname_ph')}
                          error={submitted && lastMissing}
                          onChange={(e) => patch({ lastName: e.target.value })}
                        />
                      </Field>
                    </div>
                    <div className='guest-detail__row'>
                      <Field label={t('add_person_phone')}>
                        <PhoneInput
                          countries={countries}
                          dialCode={draft.dialCode}
                          onDialCodeChange={(dial) => patch({ dialCode: dial })}
                          value={draft.mobileNumber}
                          onChange={(digits) => patch({ mobileNumber: digits })}
                          placeholder={t('add_person_phone_ph')}
                          error={submitted && contactMissing}
                          hideBadge
                        />
                      </Field>
                      <Field label={t('guest_label_email')}>
                        <Input
                          type='email'
                          iconStart='mail'
                          value={draft.email}
                          placeholder={t('add_person_email_ph')}
                          error={submitted && contactMissing}
                          onChange={(e) => patch({ email: e.target.value })}
                        />
                      </Field>
                    </div>
                    {submitted && contactMissing && (
                      <p className='guest-detail__error'>
                        <Icon name='circle-alert' size={14} />
                        {t('add_guest_err_contact')}
                      </p>
                    )}
                  </>
                ) : (
                  <dl className='guest-detail__dl'>
                    <div className='guest-detail__field'>
                      <dt className='guest-detail__label'>{t('guest_label_email')}</dt>
                      <dd className='guest-detail__value'>{detail.email || '—'}</dd>
                    </div>
                    <div className='guest-detail__field'>
                      <dt className='guest-detail__label'>{t('people_col_phone')}</dt>
                      <dd className='guest-detail__value' dir='ltr'>
                        {detail.phone || '—'}
                      </dd>
                    </div>
                  </dl>
                )}
              </div>
            </Card>
          </div>
        )
      )}
    </Drawer>
  );
};

export default GuestDetailDrawer;
